/**
 * Agent Manager for Ameen Insurance Assistant
 * 
 * Core orchestration class that manages agent lifecycle, transitions,
 * WebSocket connections, and conversation history.
 * 
 * THIS IS THE SINGLE SOURCE OF TRUTH FOR TOOL HANDLING.
 * All tool calls are routed through AgentNodes, not through UI components.
 * 
 * Supports two modes:
 * 1. Legacy mode: Single API key, one client for all agents
 * 2. Ephemeral token mode: Per-agent tokens, new client on each transition
 */

import { EventEmitter } from 'eventemitter3';
import { LiveConnectConfig } from '@google/genai';
import { GenAILiveClient } from '../lib/genai-live-client';
import { logger } from '../utils/logger';
import { fetchGeminiToken, TokenContextData, getContextForTokenRequest } from '../lib/token-service';
import { AgentConnectionPool, createConnectionPool, PooledConnection } from '../lib/agent-connection-pool';
import { TokenResponse } from '../types';
import { DraftProfile } from '../services/draftStorageService';
import {
  AgentNode,
  AgentContext,
  AgentManagerState,
  AgentTransitionEvent,
  TransitionData,
  ConversationHistory,
  ToolCallResult,
  VoiceGender,
  UIStateManager,
  FlowStep,
  LoadingType,
  buildLiveConfig,
  createInitialContext,
  addToHistory,
  getHistoryText,
} from './types';
import { validateEntryGuardrails, findValidTransition } from './guardrails';

/** Policy flow step order (current_step 0..5). Survey/upload belong to vehicle inspection agent, not policy. */
const POLICY_FLOW_STEPS: FlowStep[] = [
  'package_selection',
  'national_id_front',
  'national_id_back',
  'driving_license',
  'vehicle_license_front',
  'vehicle_license_back',
];

/**
 * Events emitted by AgentManager
 */
export interface AgentManagerEvents {
  // Agent lifecycle events
  agentActivated: (agentId: string, context: AgentContext) => void;
  agentDeactivated: (agentId: string, context: AgentContext) => void;

  // Transition events
  transitionStarted: (event: AgentTransitionEvent) => void;
  transitionCompleted: (event: AgentTransitionEvent) => void;
  transitionFailed: (event: AgentTransitionEvent, errors: string[]) => void;

  // Connection events
  connected: () => void;
  disconnected: () => void;
  reconnecting: (agentId: string) => void;

  // State change
  stateChanged: (state: AgentManagerState) => void;

  // Tool call events (forwarded from agent)
  toolCall: (result: ToolCallResult) => void;

  // UI state change events
  flowStepChanged: (flowStep: FlowStep) => void;
  companiesChanged: (companies: any[]) => void;
  workshopsChanged: (workshops: any[]) => void;

  // Audio events (forwarded from client)
  audio: (data: ArrayBuffer) => void;
  interrupted: () => void;

  // Error events
  error: (error: Error) => void;

  // Session resumption events
  resumeFailed: () => void;
  reconnected: () => void;
}

/**
 * AgentManager configuration
 */
export interface AgentManagerConfig {
  client: GenAILiveClient;
  model: string;
  initialAgentId: string;
  agents: Map<string, AgentNode>;
  userData?: TransitionData['userData'];
  voiceGender?: VoiceGender;
  userLocation?: TransitionData['userLocation'];
  // UI State Manager for tool handlers to update UI
  uiStateManager?: UIStateManager;
  // Ephemeral token mode configuration
  useEphemeralTokens?: boolean;
  backendUrl?: string;
}

/**
 * AgentManager - Orchestrates multi-agent flows
 */
export class AgentManager extends EventEmitter<AgentManagerEvents> {
  private client: GenAILiveClient;
  private model: string;
  private agents: Map<string, AgentNode>;
  private state: AgentManagerState;
  private isInitialized: boolean = false;
  private uiStateManager?: UIStateManager;

  // Ephemeral token mode configuration
  private useEphemeralTokens: boolean = false;
  private backendUrl: string = '';
  private voiceGender: VoiceGender = 'male';

  // Connection pool for zero-latency transitions
  private connectionPool: AgentConnectionPool | null = null;

  // Flag to suppress disconnect events during seamless transitions
  private isSeamlessTransition: boolean = false;

  // Guard to prevent tryResumeSession() from running twice concurrently
  // (goAway sets it to true; the close event that follows sees it and skips)
  private _isResuming: boolean = false;

  // Queued transition waiting for audio completion
  private queuedTransition: {
    targetAgentId: string;
    reason: string;
    transitionData?: Partial<TransitionData>;
  } | null = null;

  // Track cancelled tool calls to prevent sending responses (fix for 1008 error)
  private cancelledToolCalls: Set<string> = new Set();

  // UI event queue for handling events during transitions (fix for "agent not subscribed" error)
  private pendingUIEvents: Array<{ type: string; data: any; timestamp: number }> = [];
  private uiEventQueueMaxSize: number = 10;
  private uiEventQueueTimeout: number = 5000; // 5 seconds max age for queued events

  constructor(config: AgentManagerConfig) {
    super();

    this.client = config.client;
    this.model = config.model;
    this.agents = config.agents;
    this.uiStateManager = config.uiStateManager;
    this.useEphemeralTokens = config.useEphemeralTokens || false;
    this.backendUrl = config.backendUrl || '';
    this.voiceGender = config.voiceGender || 'male';

    // Initialize state
    this.state = {
      currentAgentId: null,
      context: createInitialContext(config.userData, config.voiceGender),
      isConnected: false,
      isTransitioning: false,
      transitionHistory: [],
      flowStep: 'idle',
      companies: [],
      contractedWorkshops: [],
    };

    // Set initial location if provided
    if (config.userLocation) {
      this.state.context.transitionData.userLocation = config.userLocation;
    }

    // Bind client events
    this.setupClientEvents();

    // Initialize connection pool for ephemeral token mode
    if (this.useEphemeralTokens && this.backendUrl) {
      this.connectionPool = createConnectionPool({
        backendUrl: this.backendUrl,
        voiceGender: this.voiceGender,
        maxPoolSize: 5,
        tokenTTL: 45000, // 45 seconds
      });
      logger.info('[AgentManager] Connection pool initialized for zero-latency transitions');
    }

    logger.info('[AgentManager] Initialized with agents:', Array.from(this.agents.keys()));
    logger.info('[AgentManager] Ephemeral token mode:', this.useEphemeralTokens);
  }

  /**
   * Setup event handlers for the GenAI client
   */
  private setupClientEvents(): void {
    this.client.on('open', () => {
      this.state.isConnected = true;
      this.emit('connected');
      this.emitStateChange();
      logger.connection('[AgentManager] Client connected');
    });

    this.client.on('close', () => {
      logger.connection(`[AgentManager] Client close event - isSeamlessTransition=${this.isSeamlessTransition}, isTransitioning=${this.state.isTransitioning}`);
      if (!this.isSeamlessTransition) {
        this.state.isConnected = false;
        this.emitStateChange();
        // If GoAway already started a resume, the close event is expected — don't start a second attempt
        if (!this._isResuming) {
          this.tryResumeSession();
        } else {
          logger.connection('[AgentManager] Close after GoAway — resume already in progress, skipping');
        }
      } else {
        logger.connection('[AgentManager] Client closed (seamless transition - NOT emitting disconnect)');
      }
    });

    this.client.on('error', (error) => {
      logger.error('[AgentManager] Client error:', error);
      this.emit('error', new Error(error.message));
    });

    this.client.on('goAway', () => {
      logger.connection('[AgentManager] GoAway received — attempting proactive resume');
      this.tryResumeSession();
    });

    // CRITICAL: Tool calls are handled here by AgentManager, NOT in UI components
    this.client.on('toolcall', async (toolCall) => {
      await this.handleToolCall(toolCall);
    });

    // Handle tool call cancellations (fix for 1008 error)
    this.client.on('toolcallcancellation', (cancellation) => {
      if (cancellation.ids) {
        logger.tool('[AgentManager] Tool call cancelled by server:', cancellation.ids);
        cancellation.ids.forEach(id => this.cancelledToolCalls.add(id));
      }
    });

    this.client.on('turncomplete', () => {
      logger.info('[AgentManager] Turn complete in agent:', this.state.currentAgentId);

      // Clear cancelled tool calls for the new turn
      if (this.cancelledToolCalls.size > 0) {
        this.cancelledToolCalls.clear();
      }

      // If there's a queued transition waiting for audio completion, execute it now
      if (this.queuedTransition) {
        logger.info('[AgentManager] Audio playback complete, executing queued transition to:', this.queuedTransition.targetAgentId);

        const { targetAgentId, reason, transitionData } = this.queuedTransition;
        this.queuedTransition = null; // Clear queue

        // Small delay to ensure audio buffer finishes playing
        setTimeout(() => {
          this.transitionTo(targetAgentId, reason, transitionData);
        }, 1000); // 1 second buffer for audio playback completion
      }
    });

    this.client.on('content', (content) => {
      // Track assistant messages in history
      if (content.modelTurn?.parts) {
        const textParts = content.modelTurn.parts
          .filter((p: any) => p.text)
          .map((p: any) => p.text)
          .join(' ');

        if (textParts) {
          this.addMessageToHistory('assistant', textParts);
        }
      }
    });

    // Forward audio events for speaker output
    this.client.on('audio', (data: ArrayBuffer) => {
      logger.audio('[AgentManager] Forwarding audio data:', data.byteLength, 'bytes');
      this.emit('audio', data);
    });

    // Forward interruption events
    this.client.on('interrupted', () => {
      logger.audio('[AgentManager] Forwarding audio interruption');
      this.emit('interrupted');
    });
  }

  /**
   * Attempt to resume the current session using a previously saved handle.
   * Called on unexpected close and proactively on GoAway.
   * Emits 'reconnected' on success, 'resumeFailed' on any failure so AgentContext
   * can fall back to a normal start() / startFromDraft() flow.
   */
  private async tryResumeSession(): Promise<void> {
    if (this._isResuming) return; // already in progress — ignore duplicate trigger
    this._isResuming = true;

    const handle = localStorage.getItem("gemini_resume_handle");
    if (!handle) {
      this._isResuming = false;
      this.emit("resumeFailed");
      return;
    }

    const agentId = this.state.currentAgentId;
    if (!agentId) {
      localStorage.removeItem("gemini_resume_handle");
      this._isResuming = false;
      this.emit("resumeFailed");
      return;
    }

    const agent = this.agents.get(agentId);
    if (!agent) {
      localStorage.removeItem("gemini_resume_handle");
      this._isResuming = false;
      this.emit("resumeFailed");
      return;
    }

    try {
      const tokenData = await fetchGeminiToken(
        this.backendUrl,
        agentId,
        this.voiceGender,
        this.state.context.transitionData
      );

      // Disconnect while preserving the handle so connect() can read it from localStorage
      this.client.disconnect(true);

      // Re-bind events to the same client instance before reconnecting
      this.setupClientEvents();

      // connect() reads the handle from localStorage → passes to connectWithEphemeralToken()
      const success = await this.client.connect(tokenData.model, {});
      if (success) {
        this.state.isConnected = true;
        this._isResuming = false;
        this.emit("reconnected");
      } else {
        throw new Error("connect() returned false during resume");
      }
    } catch (err) {
      console.warn("[AgentManager] Session resume failed:", err);
      localStorage.removeItem("gemini_resume_handle");
      this._isResuming = false;
      this.emit("resumeFailed");
    }
  }

  /**
   * Emit state change event
   */
  private emitStateChange(): void {
    this.emit('stateChanged', { ...this.state });
  }

  /**
   * Get current state (immutable copy)
   */
  public getState(): AgentManagerState {
    return { ...this.state };
  }

  /**
   * Get current agent
   */
  public getCurrentAgent(): AgentNode | null {
    if (!this.state.currentAgentId) return null;
    return this.agents.get(this.state.currentAgentId) || null;
  }

  /**
   * Get current context
   */
  public getContext(): AgentContext {
    return { ...this.state.context };
  }

  /**
   * Update transition data (for UI-triggered updates)
   */
  public updateTransitionData(data: Partial<TransitionData>): void {
    this.state.context.transitionData = {
      ...this.state.context.transitionData,
      ...data,
    };
    this.emitStateChange();
  }

  /**
   * Set the UI state manager (called when Altair mounts and registers its UI callbacks).
   * Tool handlers use this to update flow step, companies, etc., so the popup shows.
   * When called during or after a transition, flushes any pending UI events.
   */
  public setUIStateManager(manager: UIStateManager): void {
    this.uiStateManager = manager;
    logger.info('[AgentManager] UIStateManager registered for agent:', this.state.currentAgentId);

    // Flush any pending UI events that were queued during transition
    this.flushPendingUIEvents();
  }

  /**
   * Queue a UI event to be processed when UIStateManager becomes available
   * Used during transitions when UI components may not be subscribed yet
   */
  private queueUIEvent(type: string, data: any): void {
    // Clean up expired events
    const now = Date.now();
    this.pendingUIEvents = this.pendingUIEvents.filter(
      event => (now - event.timestamp) < this.uiEventQueueTimeout
    );

    // Add new event
    if (this.pendingUIEvents.length < this.uiEventQueueMaxSize) {
      this.pendingUIEvents.push({ type, data, timestamp: now });
      logger.info('[AgentManager] Queued UI event during transition:', type);
    } else {
      logger.warn('[AgentManager] UI event queue full, dropping oldest event');
      this.pendingUIEvents.shift();
      this.pendingUIEvents.push({ type, data, timestamp: now });
    }
  }

  /**
   * Flush pending UI events after transition completes and UI is ready
   */
  private flushPendingUIEvents(): void {
    if (this.pendingUIEvents.length === 0) return;

    logger.info('[AgentManager] Flushing', this.pendingUIEvents.length, 'pending UI events');
    const now = Date.now();

    for (const event of this.pendingUIEvents) {
      // Skip expired events
      if ((now - event.timestamp) >= this.uiEventQueueTimeout) {
        logger.warn('[AgentManager] Skipping expired UI event:', event.type);
        continue;
      }

      // Process the event
      this.processQueuedUIEvent(event.type, event.data);
    }

    // Clear the queue
    this.pendingUIEvents = [];
  }

  /**
   * Process a single queued UI event
   */
  private processQueuedUIEvent(type: string, data: any): void {
    if (!this.uiStateManager) {
      logger.warn('[AgentManager] Cannot process UI event, no UIStateManager:', type);
      return;
    }

    try {
      switch (type) {
        case 'flowStep':
          this.uiStateManager.setFlowStep(data);
          break;
        case 'companies':
          this.uiStateManager.setCompanies(data);
          break;
        case 'workshops':
          this.uiStateManager.setContractedWorkshops(data);
          break;
        default:
          logger.warn('[AgentManager] Unknown queued UI event type:', type);
      }
    } catch (error) {
      logger.error('[AgentManager] Error processing queued UI event:', type, error);
    }
  }

  /**
   * Check if UI state manager is available and subscribed
   */
  public isUIReady(): boolean {
    return this.uiStateManager !== undefined && !this.state.isTransitioning;
  }

  /**
   * Create a proxy UIStateManager that queues events when real manager unavailable.
   * This ensures tool calls during transitions don't lose UI updates.
   */
  private createUIStateProxy(): UIStateManager | undefined {
    if (!this.uiStateManager && !this.state.isTransitioning) {
      // No manager and not transitioning - return undefined
      return undefined;
    }

    // If we have a real manager and not transitioning, just return it
    if (this.uiStateManager && !this.state.isTransitioning) {
      return this.uiStateManager;
    }

    // Create proxy that queues critical events during transitions
    const self = this;
    const realManager = this.uiStateManager;

    // If we have no real manager at all, create a queueing-only proxy
    const proxy: UIStateManager = {
      // Critical methods that need queueing during transitions
      setFlowStep: (step: FlowStep) => {
        if (realManager && !self.state.isTransitioning) {
          realManager.setFlowStep(step);
        } else {
          logger.info('[AgentManager] Queueing setFlowStep during transition:', step);
          self.queueUIEvent('flowStep', step);
        }
      },
      setCompanies: (companies: any[]) => {
        if (realManager && !self.state.isTransitioning) {
          realManager.setCompanies(companies);
        } else {
          logger.info('[AgentManager] Queueing setCompanies during transition');
          self.queueUIEvent('companies', companies);
        }
      },
      setContractedWorkshops: (workshops: any[]) => {
        if (realManager && !self.state.isTransitioning) {
          realManager.setContractedWorkshops(workshops);
        } else {
          logger.info('[AgentManager] Queueing setContractedWorkshops during transition');
          self.queueUIEvent('workshops', workshops);
        }
      },

      // Pass-through methods (these either don't need queueing or have fallbacks)
      getFlowStep: () => realManager?.getFlowStep() || 'idle' as FlowStep,
      getCompanies: () => realManager?.getCompanies() || [],
      getContractedWorkshops: () => realManager?.getContractedWorkshops() || [],
      getSurveyImages: () => realManager?.getSurveyImages() || [],
      setSurveyImages: (images: any[]) => realManager?.setSurveyImages(images),
      getClaimSurveyImages: () => realManager?.getClaimSurveyImages() || [],
      setClaimSurveyImages: (images: any[]) => realManager?.setClaimSurveyImages(images),
      getCustomerNationalIdData: () => realManager?.getCustomerNationalIdData(),
      setCustomerNationalIdData: (updater: (prev: any) => any) => realManager?.setCustomerNationalIdData(updater),
      getClaimPolicyData: () => realManager?.getClaimPolicyData(),
      setClaimPolicyData: (data: any) => realManager?.setClaimPolicyData(data),
      showPolicyNumber: (num: string, title: string, msg: string, onConfirm: () => void) =>
        realManager?.showPolicyNumber(num, title, msg, onConfirm),
      showExtractedDataConfirmation: (data: any[], title: string, docType: string, onConfirm: (d?: any[]) => void) =>
        realManager?.showExtractedDataConfirmation(data, title, docType, onConfirm),
      showLoading: (message: string, type?: LoadingType) => realManager?.showLoading(message, type),
      hideLoading: () => realManager?.hideLoading(),
      onStartCameraStream: () => realManager?.onStartCameraStream() || Promise.resolve(false),
      onTakeVehiclePicture: (side: string, flowStep?: FlowStep) => realManager?.onTakeVehiclePicture(side, flowStep) || Promise.resolve(null),
      onCloseCameraStream: () => realManager?.onCloseCameraStream(),
      saveToSessionStorage: (key: string, data: any) => realManager?.saveToSessionStorage(key, data),
      getFromSessionStorage: (key: string) => realManager?.getFromSessionStorage(key),
      getClient: () => realManager?.getClient(),
    };

    return proxy;
  }

  /**
   * Set flow step (for UI updates)
   */
  public setFlowStep(flowStep: FlowStep): void {
    this.state.flowStep = flowStep;
    this.emit('flowStepChanged', flowStep);
    this.emitStateChange();
  }

  /**
   * Set companies (for package selection UI)
   */
  public setCompanies(companies: any[]): void {
    this.state.companies = companies;
    this.emit('companiesChanged', companies);
    this.emitStateChange();
  }

  /**
   * Set contracted workshops (for claim flow)
   */
  public setContractedWorkshops(workshops: any[]): void {
    this.state.contractedWorkshops = workshops;
    this.emit('workshopsChanged', workshops);
    this.emitStateChange();
  }

  /**
   * Add message to conversation history
   */
  public addMessageToHistory(role: 'user' | 'assistant' | 'system', content: string): void {
    this.state.context.conversationHistory = addToHistory(
      this.state.context.conversationHistory,
      { role, content, agentId: this.state.currentAgentId || undefined }
    );
  }

  /**
   * Initialize and start with the initial agent
   */
  public async start(): Promise<boolean> {
    if (this.isInitialized) {
      logger.warn('[AgentManager] Already initialized');
      return true;
    }

    const initialAgentId = Array.from(this.agents.keys())[0];
    if (!initialAgentId) {
      throw new Error('No agents registered');
    }

    logger.info('[AgentManager] Starting with initial agent:', initialAgentId);

    const success = await this.activateAgent(initialAgentId);
    if (success) {
      this.isInitialized = true;
    }

    return success;
  }

  /**
   * Start from a saved draft — connect directly to the target agent
   * with all collected data pre-loaded into transition data and context.
   */
  public async startFromDraft(draft: DraftProfile): Promise<boolean> {
    if (this.isInitialized) {
      logger.warn('[AgentManager] Already initialized');
      return true;
    }

    const targetAgentId = draft.currentAgentId;
    const agent = this.agents.get(targetAgentId);
    if (!agent) {
      logger.error('[AgentManager] Draft target agent not found:', targetAgentId);
      // Fallback to normal start
      return this.start();
    }

    logger.info(`[AgentManager] 📂 Starting from draft: ${draft.flowType} / ${targetAgentId} / step: ${draft.currentFlowStep}`);

    // Pre-populate transition data from draft
    this.state.context.transitionData = {
      ...this.state.context.transitionData,
      ...draft.transitionData,
    };

    // Store resume metadata in customData for buildContextForToken to pick up
    this.state.context.transitionData.customData = {
      ...this.state.context.transitionData.customData,
      resume_draft: true,
      resume_flow_type: draft.flowType,
      resume_step: draft.currentFlowStep,
      completed_documents: draft.completedDocuments || [],
      completed_underwriting_questions: Object.keys(draft.underwritingAnswers || {}),
      draft_customer_data: draft.customerNationalIdData,
      draft_companies_data: draft.companiesData,
      draft_selected_package: draft.selectedPackage,
      draft_claim_policy_data: draft.claimPolicyData,
      draft_survey_images: draft.surveyImages,
      draft_conversation_summary: draft.conversationSummary,
    };

    // Carry over the draft's ID so tryAutoSaveDraft upserts to the same record
    // instead of generating a new UUID (which would create a duplicate entry).
    this.activeDraftId = draft.id;

    // Activate the target agent directly (bypasses greeting)
    const success = await this.activateAgent(targetAgentId);
    if (success) {
      this.isInitialized = true;
    }

    return success;
  }

  /**
   * Stop and cleanup
   */
  public async stop(): Promise<void> {
    logger.info('[AgentManager] Stopping...');

    if (this.state.currentAgentId) {
      const currentAgent = this.getCurrentAgent();
      if (currentAgent?.onDeactivate) {
        await currentAgent.onDeactivate(this.state.context);
      }
    }

    this.client.disconnect();
    this._isResuming = false;

    // Clean up connection pool
    if (this.connectionPool) {
      this.connectionPool.destroy();
      this.connectionPool = null;
    }

    // Reset all state for fresh reconnection
    this.state.currentAgentId = null;
    this.state.isConnected = false;
    this.state.isTransitioning = false;
    this.isInitialized = false;

    // Clear conversation history to prevent repetition on reconnect
    this.state.context.conversationHistory = { messages: [] };

    // Clear transition data except language preference and user data
    const { language, userData, voiceGender } = this.state.context.transitionData;
    this.state.context.transitionData = {
      language,
      userData,
      voiceGender,
    };

    // Reset UI state so AgentContext (which listens to stateChanged) receives idle values
    // and does NOT override the idle reset we set from the disconnect/error handlers.
    this.state.flowStep = 'idle';
    this.state.companies = [];
    this.state.contractedWorkshops = [];

    // Clear any pending UI events that were queued during this session
    this.pendingUIEvents = [];
    this.queuedTransition = null;

    logger.info('[AgentManager] State reset - ready for fresh connection');
    this.emitStateChange();
  }

  /**
   * Activate a specific agent
   */
  private async activateAgent(agentId: string): Promise<boolean> {
    const agent = this.agents.get(agentId);
    if (!agent) {
      logger.error('[AgentManager] Agent not found:', agentId);
      return false;
    }

    // Validate entry guardrails
    const errors = validateEntryGuardrails(agent.entryGuardrails, this.state.context);
    if (errors.length > 0) {
      logger.error('[AgentManager] Entry guardrails failed for agent:', agentId, errors);
      return false;
    }

    logger.info('[AgentManager] Activating agent:', agentId);

    // NOTE: Do NOT disconnect here - the disconnect is handled inside
    // activateAgentWithToken AFTER setting isSeamlessTransition=true
    // to prevent close events from triggering UI changes

    if (this.useEphemeralTokens) {
      // EPHEMERAL TOKEN MODE: Fetch new token and create new client
      return this.activateAgentWithToken(agentId, agent);
    } else {
      // LEGACY MODE: Use existing client with new config
      // Disconnect before reconnecting with new config
      this.client.disconnect();
      return this.activateAgentLegacy(agentId, agent);
    }
  }

  /**
   * Build context data to pass to backend for system instruction injection.
   * For policy_agent and claim_agent we pass minimal context only (each flow is a fresh start; other flow's data would skip steps).
   * @param targetAgentId - Agent we are requesting the token for; when 'policy_agent' or 'claim_agent', context is stripped to minimal.
   */
  private buildContextForToken(targetAgentId?: string): TokenContextData {
    const { transitionData, conversationHistory } = this.state.context;
    const {
      language, userData, userLocation, caller_agent, flow_state,
      damage_description, selected_package, customer_data, survey_images,
      policy_number, claim_request_num, return_to_menu
    } = transitionData;

    const contextData: TokenContextData = {};

    // Core context - ALL flows
    if (language) {
      contextData.language = language;
    }

    const conversationSummary = getHistoryText(conversationHistory, 5);
    if (conversationSummary) {
      contextData.conversation_summary = conversationSummary;
    }

    if (userData) {
      contextData.user_name = userData.username;
      contextData.user_mobile = userData.mobileNumber;
    }

    if (userLocation?.address) {
      contextData.user_location = userLocation.address;
    }

    // Ameen-specific context
    if (caller_agent) {
      contextData.caller_agent = caller_agent;
    }

    if (flow_state) {
      contextData.flow_state = flow_state;
    }

    if (damage_description) {
      contextData.damage_description = damage_description;
    }

    if (selected_package) {
      contextData.selected_package = selected_package;
    }

    if (customer_data) {
      contextData.customer_data = customer_data;
    }

    if (survey_images) {
      contextData.survey_images = survey_images;
    }

    if (policy_number) {
      contextData.policy_number = policy_number;
    }

    if (claim_request_num) {
      contextData.claim_request_num = claim_request_num;
    }

    if (return_to_menu) {
      contextData.return_to_menu = return_to_menu;
    }

    // Resume draft context — pass to backend for system instruction injection
    const customData = transitionData.customData;
    if (customData?.resume_draft) {
      contextData.resume_draft = true;
      contextData.resume_flow_type = customData.resume_flow_type;
      contextData.resume_step = customData.resume_step;
      contextData.completed_documents = customData.completed_documents;
      contextData.completed_underwriting_questions = customData.completed_underwriting_questions;
      if (customData.draft_customer_data) {
        contextData.customer_data = customData.draft_customer_data;
      }
      if (customData.draft_conversation_summary) {
        contextData.conversation_summary = customData.draft_conversation_summary;
      }
    }

    return getContextForTokenRequest(targetAgentId || '', contextData);
  }

  /**
   * Detect context changes between pooled connection and current context
   * Returns a system message to inject if context has changed, null otherwise
   */
  private detectContextChanges(pooledContext: TokenContextData, currentContext: TokenContextData): string | null {
    const changes: string[] = [];

    // 1. LANGUAGE (Critical - affects all flows)
    if (currentContext.language && pooledContext.language !== currentContext.language) {
      const langNames: Record<string, string> = {
        'ar': 'Arabic (Egyptian Dialect)',
        'en': 'English'
      };
      changes.push(`**CRITICAL CONTEXT UPDATE:**`);
      changes.push(`**User Language:** ${currentContext.language} (${langNames[currentContext.language] || currentContext.language})`);
      changes.push(`**You MUST speak ONLY in ${langNames[currentContext.language] || currentContext.language} from now on.**`);
      if (currentContext.language === 'ar') {
        changes.push(`**CRITICAL: Use Egyptian dialect for Arabic.**`);
      }
    }

    // 2. USER DATA (affects all flows)
    if (currentContext.user_name && pooledContext.user_name !== currentContext.user_name) {
      changes.push(`**User Name:** ${currentContext.user_name}`);
    }
    if (currentContext.user_mobile && pooledContext.user_mobile !== currentContext.user_mobile) {
      changes.push(`**User Mobile:** ${currentContext.user_mobile}`);
    }
    if (currentContext.user_location && pooledContext.user_location !== currentContext.user_location) {
      changes.push(`**User Location:** ${currentContext.user_location}`);
    }

    // 3. AMEEN-SPECIFIC CONTEXT
    if (currentContext.caller_agent && pooledContext.caller_agent !== currentContext.caller_agent) {
      changes.push(`**Caller Agent:** ${currentContext.caller_agent}`);
    }
    if (currentContext.flow_state && !pooledContext.flow_state) {
      changes.push(`**Flow State:** ${JSON.stringify(currentContext.flow_state)}`);
    }
    if (currentContext.damage_description && !pooledContext.damage_description) {
      changes.push(`**Damage Description:** ${currentContext.damage_description}`);
    }
    if (currentContext.selected_package && !pooledContext.selected_package) {
      changes.push(`**Selected Package:** ${JSON.stringify(currentContext.selected_package)}`);
    }

    // 4. CONVERSATION HISTORY (only if significantly different)
    if (currentContext.conversation_summary && pooledContext.conversation_summary !== currentContext.conversation_summary) {
      changes.push(`\n**Previous Conversation Context:**`);
      changes.push(currentContext.conversation_summary);
    }

    return changes.length > 0 ? changes.join('\n') : null;
  }

  /**
   * Warm the connection pool for likely next agents
   * Called after an agent is activated to pre-connect next likely agents
   */
  private warmPoolForNextAgents(currentAgentId: string, context: TokenContextData): void {
    if (!this.connectionPool) return;

    logger.info('[AgentManager] Warming pool for likely next agents from:', currentAgentId);

    // Fire and forget - don't block activation
    this.connectionPool.warmPool(currentAgentId, context).catch(error => {
      logger.warn('[AgentManager] Pool warming failed:', error);
    });
  }

  /**
   * Activate agent using ephemeral token (secure mode)
   * Uses connection pool for instant transitions when available
   */
  private async activateAgentWithToken(agentId: string, agent: AgentNode): Promise<boolean> {
    logger.info('[AgentManager] Activating agent with ephemeral token:', agentId);

    // DEBUG: Log current connection state
    logger.connection(`[AgentManager] Current client ID: ${this.client?.id || 'none'}, Status: ${this.client?.status}`);

    try {
      // Build context data from current state (minimal for claim_agent so claim flow starts fresh)
      const contextData = this.buildContextForToken(agentId);
      logger.info('[AgentManager] Context data for token:',
        contextData.language ? `language=${contextData.language}` : 'no language',
        contextData.conversation_summary ? 'with history' : 'no history'
      );

      // Mark as seamless transition - suppress disconnect events
      this.isSeamlessTransition = true;

      // TRY 1: Check if we have a pre-warmed connection in the pool (INSTANT)
      const warmConnection = this.connectionPool?.getWarmConnection(agentId);
      if (warmConnection && warmConnection.isConnected) {
        logger.info('[AgentManager] 🚀 SEAMLESS INSTANT SWITCH - Using pre-warmed connection for:', agentId);

        // Disconnect old client (close event suppressed by isSeamlessTransition flag)
        this.client.disconnect();

        // Use the pre-warmed client
        this.client = warmConnection.client;
        this.model = warmConnection.model;

        // Re-bind client events to this manager
        this.setupClientEvents();

        // Keep isConnected = true (don't change it if already connected)
        if (!this.state.isConnected) {
          this.state.isConnected = true;
          this.emit('connected');
        }

        // Update state
        this.state.currentAgentId = agentId;
        this.state.context.previousAgentId = this.state.currentAgentId;
        this.emitStateChange();

        // Check if context changed since connection was created
        const contextDelta = this.detectContextChanges(warmConnection.context, contextData);
        if (contextDelta) {
          logger.info('[AgentManager] Context changed, injecting update into connection');
          // Send context update as system message (invisible to user)
          // Use turnComplete: false so AI waits for initial prompt before responding
          this.client.send({ text: `[SYSTEM CONTEXT UPDATE - Do not read this aloud]\n${contextDelta}` }, false);
          // Small delay before initial prompt to let AI process context
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Call onActivate hook
        if (agent.onActivate) {
          await agent.onActivate(this.state.context);
        }

        this.emit('agentActivated', agentId, this.state.context);
        logger.info('[AgentManager] Agent activated (seamless instant) with token:', agentId);

        // Send initial prompt
        const initialPrompt = warmConnection.initialPrompt || agent.initialPrompt;
        if (initialPrompt) {
          logger.info('[AgentManager] Sending initial prompt for agent:', agentId);
          await new Promise(resolve => setTimeout(resolve, 50));
          this.client.send({ text: initialPrompt }, true);
        }

        // Warm pool for next likely agents in background
        this.warmPoolForNextAgents(agentId, contextData);

        // Reset seamless flag
        this.isSeamlessTransition = false;
        return true;
      }

      // TRY 2: Check if connection is pending (wait for it)
      const pendingConnection = await this.connectionPool?.waitForConnection(agentId);
      if (pendingConnection && pendingConnection.isConnected) {
        logger.info('[AgentManager] ⏳ SEAMLESS PENDING SWITCH - Using pending connection for:', agentId);

        // Disconnect old client (close event suppressed by isSeamlessTransition flag)
        this.client.disconnect();

        this.client = pendingConnection.client;
        this.model = pendingConnection.model;
        this.setupClientEvents();

        // Keep isConnected = true (don't change it if already connected)
        if (!this.state.isConnected) {
          this.state.isConnected = true;
          this.emit('connected');
        }

        this.state.currentAgentId = agentId;
        this.state.context.previousAgentId = this.state.currentAgentId;
        this.emitStateChange();

        // Inject context delta if needed
        const contextDelta = this.detectContextChanges(pendingConnection.context, contextData);
        if (contextDelta) {
          logger.info('[AgentManager] Context changed for pending connection, injecting update');
          // Use turnComplete: false so AI waits for initial prompt before responding
          this.client.send({ text: `[SYSTEM CONTEXT UPDATE - Do not read this aloud]\n${contextDelta}` }, false);
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        if (agent.onActivate) {
          await agent.onActivate(this.state.context);
        }

        this.emit('agentActivated', agentId, this.state.context);

        const initialPrompt = pendingConnection.initialPrompt || agent.initialPrompt;
        if (initialPrompt) {
          await new Promise(resolve => setTimeout(resolve, 50));
          this.client.send({ text: initialPrompt }, true);
        }

        this.warmPoolForNextAgents(agentId, contextData);

        // Reset seamless flag
        this.isSeamlessTransition = false;
        return true;
      }

      // TRY 3: Fallback - Fetch new token (cold path - still seamless!)
      logger.info('[AgentManager] ❄️ SEAMLESS COLD PATH - Fetching new token for:', agentId);

      // Disconnect old client silently (close event still suppressed)
      if (this.client) {
        logger.connection(`[AgentManager] Disconnecting old client: ${this.client.id}`);
        this.client.disconnect();
      }

      // Fetch new token for this agent WITH context
      const tokenData: TokenResponse = await fetchGeminiToken(
        this.backendUrl,
        agentId,
        this.voiceGender,
        contextData
      );

      logger.info(`[AgentManager] Token fetched successfully for ${agentId}. Token prefix: ${tokenData.token.substring(0, 10)}...`);

      logger.info('[AgentManager] Token fetched for agent:', agentId);
      logger.info('[AgentManager] Model locked to:', tokenData.model);

      // Create new client with the token
      this.client = new GenAILiveClient({ token: tokenData.token });

      // Re-bind client events
      this.setupClientEvents();

      // Connect (config is locked in token, so we just need to connect)
      const connected = await this.client.connect(tokenData.model, {});
      if (!connected) {
        logger.error(`[AgentManager] Failed to connect with token for agent: ${agentId}. Client ID: ${this.client.id}`);
        this.isSeamlessTransition = false; // Reset on failure
        // Update state so UI knows we're disconnected and can trigger reconnect
        this.state.isConnected = false;
        this.emit('disconnected');
        this.emitStateChange();
        return false;
      }

      logger.connection(`[AgentManager] New client connected for ${agentId}. Client ID: ${this.client.id}`);

      // Update model to what was returned by token
      this.model = tokenData.model;

      // Keep isConnected = true if already connected (seamless)
      if (!this.state.isConnected) {
        this.state.isConnected = true;
        this.emit('connected');
      }

      // Update state
      this.state.currentAgentId = agentId;
      this.state.context.previousAgentId = this.state.currentAgentId;
      this.emitStateChange();

      // Call onActivate hook
      if (agent.onActivate) {
        await agent.onActivate(this.state.context);
      }

      this.emit('agentActivated', agentId, this.state.context);
      logger.info('[AgentManager] Agent activated (seamless cold path) with token:', agentId);

      // submit_policy_agent: do NOT set policy_confirmation on activation.
      // Policy confirm popup is shown only after submit_policy_request tool returns (in SubmitPolicyAgent).

      const { transitionData } = this.state.context;

      // Restore policy flow step when returning to policy_agent (e.g. user switched back; no return from inspection)
      if (agentId === 'policy_agent' && transitionData?.flow_state?.current_step !== undefined) {
        const currentStep = Number(transitionData.flow_state.current_step);
        const step: FlowStep =
          currentStep < POLICY_FLOW_STEPS.length - 1
            ? POLICY_FLOW_STEPS[currentStep + 1]
            : 'idle';
        this.setFlowStep(step);
        logger.info('[AgentManager] Restored policy flow step from flow_state:', step, '(current_step:', currentStep, ')');
      }

      // Restore document flow step when activating document_agent (steps 1–5 = document steps)
      if (agentId === 'document_agent' && transitionData?.flow_state?.current_step !== undefined) {
        const currentStep = Number(transitionData.flow_state.current_step);
        const step: FlowStep =
          currentStep >= 0 && currentStep < POLICY_FLOW_STEPS.length
            ? POLICY_FLOW_STEPS[currentStep]
            : 'national_id_front';
        this.setFlowStep(step);
        logger.info('[AgentManager] Restored document flow step from flow_state:', step, '(current_step:', currentStep, ')');
      }

      // Restore UI flow step when returning to claim agent from vehicle inspection
      if (agentId === 'claim_agent') {
        const fromInspection = transitionData?.damage_description != null || transitionData?.from_inspection === true;
        if (fromInspection) {
          this.setFlowStep('idle');
          logger.info('[AgentManager] Restored claim flow step to idle (return from vehicle inspection, will proceed to workshop selection)');
        }
      }

      // Send initial prompt (use from token response or agent). Delay longer when resuming from vehicle inspection to avoid 1008.
      const initialPrompt = tokenData.initial_prompt || agent.initialPrompt;
      const isResumeFromInspection =
        (agentId === 'submit_policy_agent' || agentId === 'submit_claim_agent') &&
        (transitionData?.damage_description != null || transitionData?.from_inspection === true);
      const delayMs = isResumeFromInspection ? 500 : 100;
      if (initialPrompt) {
        logger.info('[AgentManager] Sending initial prompt for agent:', agentId, '(delay', delayMs, 'ms)');
        await new Promise(resolve => setTimeout(resolve, delayMs));
        this.client.send({ text: initialPrompt }, true);
      }

      // Warm pool for next likely agents in background
      this.warmPoolForNextAgents(agentId, contextData);

      this.isSeamlessTransition = false;
      return true;

    } catch (error: any) {
      this.isSeamlessTransition = false;
      logger.error(`[AgentManager] CRITICAL ERROR in activateAgentWithToken for ${agentId}:`, error);
      if (error?.message) logger.error(`[AgentManager] Error message: ${error.message}`);
      if (error?.code) logger.error(`[AgentManager] Error code: ${error.code}`);

      // Update state so UI knows we're disconnected and can trigger reconnect
      this.state.isConnected = false;
      this.emit('disconnected');
      this.emitStateChange();
      this.emit('error', new Error(`Failed to activate agent ${agentId}: ${error}`));
      return false;
    }
  }

  /**
   * Activate agent using legacy mode (API key, client reuse)
   */
  private async activateAgentLegacy(agentId: string, agent: AgentNode): Promise<boolean> {
    // Build the config for this agent
    const config = this.buildAgentConfig(agent);

    // Connect with new config
    try {
      const connected = await this.client.connect(this.model, config);
      if (!connected) {
        logger.error('[AgentManager] Failed to connect for agent:', agentId);
        return false;
      }
    } catch (error) {
      logger.error('[AgentManager] Connection error for agent:', agentId, error);
      return false;
    }

    // Update state
    this.state.currentAgentId = agentId;
    this.state.context.previousAgentId = this.state.currentAgentId;
    this.emitStateChange();

    // Call onActivate hook
    if (agent.onActivate) {
      await agent.onActivate(this.state.context);
    }

    this.emit('agentActivated', agentId, this.state.context);
    logger.info('[AgentManager] Agent activated:', agentId);

    // Send initial prompt to trigger AI to speak first
    if (agent.initialPrompt) {
      logger.info('[AgentManager] Sending initial prompt for agent:', agentId);
      await new Promise(resolve => setTimeout(resolve, 100));
      this.client.send({ text: agent.initialPrompt }, true);
    }

    return true;
  }

  /**
   * Build LiveConnectConfig for an agent (LEGACY MODE only)
   * In ephemeral token mode, config comes from backend
   */
  private buildAgentConfig(agent: AgentNode): LiveConnectConfig {
    const voiceGender = this.state.context.transitionData.voiceGender || 'male';

    // Inject context into system instruction if needed
    let systemInstruction = agent.systemInstruction || '';

    // Add conversation history context
    const historyText = getHistoryText(this.state.context.conversationHistory, 5);
    if (historyText) {
      systemInstruction = `${systemInstruction}\n\n**Previous Conversation Context:**\n${historyText}`;
    }

    return buildLiveConfig({
      systemInstruction,
      tools: agent.tools || [],
      voiceGender,
      responseModality: agent.responseModality,
    });
  }

  /**
   * Transition to a new agent
   */
  public async transitionTo(
    targetAgentId: string,
    reason: string,
    additionalData?: Partial<TransitionData>
  ): Promise<boolean> {
    if (this.state.isTransitioning) {
      logger.warn('[AgentManager] Already transitioning, ignoring request');
      return false;
    }

    const currentAgentId = this.state.currentAgentId || 'none';

    logger.info('[AgentManager] Transitioning from', currentAgentId, 'to', targetAgentId);

    this.state.isTransitioning = true;
    this.emitStateChange();

    const transitionEvent: AgentTransitionEvent = {
      fromAgentId: currentAgentId,
      toAgentId: targetAgentId,
      reason,
      context: { ...this.state.context },
      timestamp: new Date(),
    };

    this.emit('transitionStarted', transitionEvent);

    try {
      // Update transition data if provided
      if (additionalData) {
        this.state.context.transitionData = {
          ...this.state.context.transitionData,
          ...additionalData,
        };
      }

      this.state.context.transitionReason = reason;

      // Deactivate current agent
      const currentAgent = this.getCurrentAgent();
      if (currentAgent?.onDeactivate) {
        await currentAgent.onDeactivate(this.state.context);
      }
      this.emit('agentDeactivated', currentAgentId, this.state.context);

      // Activate new agent
      const success = await this.activateAgent(targetAgentId);

      if (success) {
        this.state.transitionHistory.push(transitionEvent);
        this.emit('transitionCompleted', transitionEvent);
        logger.info('[AgentManager] Transition completed to:', targetAgentId);
      } else {
        this.emit('transitionFailed', transitionEvent, ['Failed to activate agent']);
        logger.error('[AgentManager] Transition failed to:', targetAgentId);
      }

      return success;
    } finally {
      this.state.isTransitioning = false;
      this.emitStateChange();
    }
  }

  /**
   * Handle tool calls from the current agent
   * THIS IS THE SINGLE SOURCE OF TRUTH FOR TOOL HANDLING
   */
  private async handleToolCall(toolCall: any): Promise<void> {
    const currentAgent = this.getCurrentAgent();
    if (!currentAgent) {
      logger.warn('[AgentManager] Tool call received but no active agent');
      return;
    }

    logger.tool('[AgentManager] Tool call received:', toolCall.functionCalls?.map((fc: any) => fc.name));

    const functionCalls = toolCall.functionCalls || [];
    const responses: any[] = [];

    for (const fc of functionCalls) {
      let result: ToolCallResult;

      // Check if agent has custom handler - THIS IS WHERE ALL TOOL HANDLING HAPPENS
      // Use proxy UIStateManager to auto-queue events during transitions
      if (currentAgent.handleToolCall) {
        const uiProxy = this.createUIStateProxy();
        result = await currentAgent.handleToolCall(fc.name, fc.args, this.state.context, uiProxy);
      } else {
        // Default handling
        result = {
          toolName: fc.name,
          args: fc.args,
          result: { success: true },
        };
      }

      // Emit tool call event
      this.emit('toolCall', result);

      // Auto-save draft after successful save-point tools
      this.tryAutoSaveDraft(fc.name, result);
      // Support both immediate and audio-completion-based transitions
      if (result.triggersTransition && result.transitionTo) {
        const delayMs = result.transitionDelayMs || 0;

        if (delayMs === -1) {
          // Special value -1 means "wait for audio completion"
          logger.info('[AgentManager] Tool triggered transition to:', result.transitionTo, '- QUEUED waiting for audio completion');

          // Queue the transition to execute after turncomplete + audio buffer
          this.queuedTransition = {
            targetAgentId: result.transitionTo,
            reason: `Tool ${fc.name} triggered transition (after audio completion)`,
            transitionData: result.transitionData
          };
        } else if (delayMs > 0) {
          logger.info('[AgentManager] Tool triggered transition to:', result.transitionTo, 'with', delayMs, 'ms fixed delay');

          // Fixed delay transition (legacy fallback)
          setTimeout(() => {
            this.transitionTo(
              result.transitionTo!,
              `Tool ${fc.name} triggered transition (after fixed delay)`,
              result.transitionData
            );
          }, delayMs);
        } else {
          logger.info('[AgentManager] Tool triggered transition to:', result.transitionTo, '- immediate');

          // Immediate transition (default behavior)
          this.transitionTo(
            result.transitionTo!,
            `Tool ${fc.name} triggered transition`,
            result.transitionData
          );
        }
      }

      // Ensure output is JSON-serializable (no circular refs, undefined, etc.) to avoid server 1011
      let output: unknown = result.result;
      try {
        output = JSON.parse(JSON.stringify(result.result));
      } catch {
        output = typeof result.result === 'object' && result.result !== null
          ? { ...(result.result as Record<string, unknown>) }
          : result.result;
      }

      // Check if this tool call was cancelled during execution (e.g. user interruption)
      if (this.cancelledToolCalls.has(fc.id)) {
        logger.tool('[AgentManager] ⛔ SKIPPING cancelled tool response for:', fc.name, fc.id);
        continue;
      }

      responses.push({
        response: { output },
        id: fc.id,
        name: fc.name,
      });
    }

    // Send tool responses ONLY if we have valid responses (and they weren't all cancelled)
    if (responses.length > 0) {
      logger.tool('[AgentManager] Sending tool responses:', responses.length, 'responses');
      this.client.sendToolResponse({ functionResponses: responses });
    } else {
      logger.tool('[AgentManager] All tool responses were cancelled or empty - skipping send');
    }
  }

  // ──────────────────────────────────────────────────────────────
  // ── Auto-Save Draft Logic ────────────────────────────────────
  // ──────────────────────────────────────────────────────────────

  /** Tracks the active draft ID for upsert (so we don't create duplicates). */
  private activeDraftId: string | null = null;

  /**
   * Save-point tool names and the flow type they belong to.
   * After these tools complete successfully, we snapshot the draft.
   */
  private static readonly POLICY_SAVE_TOOLS = new Set([
    'calculate_premium',
    'go_to_document_agent',
  ]);

  private static readonly DOCUMENT_SAVE_TOOLS = new Set([
    'request_national_id_front',
    'request_national_id_back',
    'request_driving_license',
    'request_vehicle_license_front',
    'request_vehicle_license_back',
  ]);

  private static readonly CLAIM_SAVE_TOOLS = new Set([
    'request_policy_inquiry',
  ]);

  private static readonly COMPLETION_TOOLS = new Set([
    'confirm_policy_request',
    'submit_claim_survey_request',
  ]);

  /**
   * Called after each successful tool call to auto-save draft if appropriate.
   */
  private tryAutoSaveDraft(toolName: string, toolResult: ToolCallResult): void {
    try {
      // Only save on success
      if (!toolResult.result?.success) return;

      const agentId = this.state.currentAgentId;
      const userData = this.state.context.transitionData?.userData;
      const mobileNumber = userData?.mobileNumber;
      if (!mobileNumber || !agentId) return;

      let flowType: 'policy' | 'claim' | null = null;

      // Determine flow type from the tool
      if (AgentManager.POLICY_SAVE_TOOLS.has(toolName) || AgentManager.DOCUMENT_SAVE_TOOLS.has(toolName)) {
        flowType = 'policy';
      } else if (AgentManager.CLAIM_SAVE_TOOLS.has(toolName)) {
        flowType = 'claim';
      } else if (agentId === 'under_writing_question_agent' || agentId === 'vehicle_inspection_agent') {
        // Underwriting and vehicle inspection are always policy flow for now
        flowType = 'policy';
      } else if (agentId === 'submit_claim_agent') {
        flowType = 'claim';
      }

      if (!flowType) return; // Not a save-point tool

      // Detect the auto-delete scenario first
      if (AgentManager.COMPLETION_TOOLS.has(toolName)) {
        this.tryAutoDeleteDraft(toolName, mobileNumber);
        return;
      }

      // Lazy import to avoid circular deps
      const { saveDraft, buildDraftSnapshot } = require('../services/draftStorageService');

      const draft = buildDraftSnapshot({
        existingDraftId: this.activeDraftId || undefined,
        flowType,
        currentAgentId: agentId,
        currentFlowStep: this.state.flowStep || 'idle',
        mobileNumber,
        transitionData: this.state.context.transitionData,
        customerNationalIdData: this.uiStateManager?.getCustomerNationalIdData?.(),
        completedDocuments: this.getCompletedDocumentsFromFlowStep(),
        underwritingAnswers: this.state.context.transitionData?.customData?.underwritingAnswers,
        surveyImages: this.uiStateManager?.getSurveyImages?.(),
        claimPolicyData: this.uiStateManager?.getClaimPolicyData?.(),
        companiesData: this.state.companies,
        selectedPackage: this.state.context.transitionData?.selected_package,
        conversationSummary: getHistoryText(this.state.context.conversationHistory, 10),
      });

      // Track the draft ID for upserts
      this.activeDraftId = draft.id;
      // saveDraft is async but fire-and-forget (errors are handled inside)
      saveDraft(draft);
      logger.info(`[AgentManager] 💾 Auto-saved draft: ${flowType} / ${agentId} / tool: ${toolName}`);
    } catch (err) {
      logger.error('[AgentManager] Auto-save draft failed:', err);
    }
  }

  /**
   * Derive which documents have been completed based on current flow step.
   */
  private getCompletedDocumentsFromFlowStep(): string[] {
    const docOrder = [
      'national_id_front', 'national_id_back',
      'driving_license',
      'vehicle_license_front', 'vehicle_license_back',
    ];
    const currentStep = this.state.flowStep as string;
    const idx = docOrder.indexOf(currentStep);
    if (idx <= 0) return [];
    return docOrder.slice(0, idx);
  }

  /**
   * Mark the active draft as Completed on successful flow completion.
   */
  private tryAutoDeleteDraft(toolName: string, _mobileNumber: string): void {
    try {
      const { completeDraft } = require('../services/draftStorageService');
      if (
        (toolName === 'confirm_policy_request' || toolName === 'submit_claim_survey_request') &&
        this.activeDraftId
      ) {
        // completeDraft is async but fire-and-forget
        completeDraft(this.activeDraftId);
        logger.info(`[AgentManager] ✅ Draft marked completed on tool: ${toolName}`);
      }
      this.activeDraftId = null;
    } catch (err) {
      logger.error('[AgentManager] Auto-complete draft failed:', err);
    }
  }

  /**
   * Request transition by name (for self-managed transitions)
   */
  public async requestTransition(transitionName: string): Promise<boolean> {
    const currentAgent = this.getCurrentAgent();
    if (!currentAgent) {
      logger.warn('[AgentManager] No active agent for transition');
      return false;
    }

    const result = findValidTransition(
      currentAgent.exitGuardrails.transitions,
      transitionName,
      this.state.context
    );

    if (!result) {
      logger.warn('[AgentManager] No transition found for:', transitionName);

      // Try fallback
      if (currentAgent.exitGuardrails.fallback) {
        return this.transitionTo(
          currentAgent.exitGuardrails.fallback.agentId,
          `Fallback from ${transitionName}`
        );
      }
      return false;
    }

    if (result.errors.length > 0) {
      logger.error('[AgentManager] Transition guardrails failed:', result.errors);
      return false;
    }

    return this.transitionTo(result.targetAgentId, transitionName);
  }

  /**
   * Send message to current agent
   * Skips sending during transitions to prevent WebSocket errors
   */
  public send(message: string | { text: string }, turnComplete: boolean = true): void {
    // Skip during transitions to prevent WebSocket state errors (e.g., 1008)
    if (this.state.isTransitioning || this.isSeamlessTransition) {
      logger.warn('[AgentManager] Skipping message send during transition');
      return; // Message will be lost, but this prevents crashes
    }

    const text = typeof message === 'string' ? message : message.text;
    this.addMessageToHistory('user', text);
    this.client.send([{ text }], turnComplete);
  }

  /**
   * Send realtime input (audio/video)
   * Skips sending during agent transitions to prevent WebSocket errors
   */
  public sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>): void {
    // Skip during transitions to prevent "WebSocket not open" warnings
    if (this.state.isTransitioning || this.isSeamlessTransition) {
      return; // Silently skip - audio will resume after transition completes
    }

    // Skip if we have cancelled tool calls (user interrupted) to prevent 1008 errors
    if (this.cancelledToolCalls.size > 0) {
      return; // Silently skip input until turn completes and cancellations are cleared
    }

    this.client.sendRealtimeInput(chunks);
  }

  /**
   * Get client for direct access if needed
   */
  public getClient(): GenAILiveClient {
    return this.client;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.state.isConnected;
  }

  /**
   * Get all registered agents
   */
  public getAgents(): Map<string, AgentNode> {
    return new Map(this.agents);
  }
}

/**
 * Create an AgentManager instance
 */
export function createAgentManager(config: AgentManagerConfig): AgentManager {
  return new AgentManager(config);
}
